"use client";

import type { WorkspaceNavigationPayload } from "@/lib/workspace-navigation-snapshot";
import { WORKSPACE_PREPARE_TAGGED, WORKSPACE_PREPARE_TTL_MS } from "@/lib/workspace-preparation";
import { announceWorkspaceNavigation, workspaceHomePath, workspacePageId } from "@/lib/client/workspace-navigation-shell";
import type { WorkspaceDocumentMetadata } from "@/lib/workspace-document-metadata";
import { preloadWorkspacePageCode } from "./workspace-page-code-loader";
import { cancelWorkspacePageWarmup } from "@/lib/api/workspace-page-warmup";

export type WorkspaceActivationResult = { status: number; snapshot: string; fresh?: WorkspaceNavigationPayload };

/** Dev-сервер и прокси могут кратко вернуть 404, пока маршрут API собирается.
 * Два повтора переживают холодную сборку, но не скрывают настоящую удалённую
 * страницу бесконечными перезагрузками. */
const TRANSIENT_404_RETRY_DELAYS_MS = [500, 1_500] as const;

export function workspaceActivationRetryDelay(status: number, failedAttempts: number): number | null {
  return status === 404 ? (TRANSIENT_404_RETRY_DELAYS_MS[failedAttempts] ?? null) : null;
}

/** One ordered write lane per tab. onNavigate can begin the same verification
 * the destination will consume, while Next is still committing its route.
 * Queued superseded clicks do not run; a running write finishes before the next.
 */
export function createWorkspaceActivationQueue(fetcher: typeof fetch) {
  let queue: Promise<unknown> = Promise.resolve();
  let generation = 0;
  let prepared: { pageId: string; promise: Promise<WorkspaceActivationResult> } | null = null;
  // Only the latest verified top, not a page cache. A new click/recheck clears
  // it; late superseded responses cannot publish. Editors retain priority.
  let header: WorkspaceDocumentMetadata | null = null;
  const listeners = new Set<() => void>();
  const publishHeader = (value: WorkspaceDocumentMetadata | null) => {
    if (header === value) return;
    header = value;
    for (const listener of listeners) listener();
  };
  const request = (pageId: string, snapshot?: string, isCurrent: () => boolean = () => true) => {
    const ticket = generation;
    const run = async (): Promise<WorkspaceActivationResult> => {
      if (ticket !== generation || !isCurrent()) return { status: 499, snapshot: snapshot ?? "" };
      publishHeader(null);
      const response = await fetcher(`/api/workspace-pages/${encodeURIComponent(pageId)}/activate`, {
        method: "POST", credentials: "same-origin", cache: "no-store", signal: AbortSignal.timeout(15_000),
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ snapshot }),
      });
      if (ticket !== generation || !isCurrent()) return { status: 499, snapshot: snapshot ?? "" };
      if (!response.ok) return { status: response.status, snapshot: snapshot ?? "" };
      const result = await response.json() as { ok: boolean; snapshot?: string; fresh?: WorkspaceNavigationPayload; header?: WorkspaceDocumentMetadata };
      if (ticket !== generation || !isCurrent()) return { status: 499, snapshot: snapshot ?? "" };
      if (!result.ok) throw new Error("Invalid activation response");
      if (ticket === generation && isCurrent() && result.header?.pageId === pageId) publishHeader(result.header);
      // An unprepared click has no tag yet. The server returns its verified
      // tag, not a second copy of the document. The destination compares it
      // with its RSC payload before mounting; mismatches use the existing
      // fresh-response path. Older servers without a tag require rechecking.
      return { status: 200, snapshot: result.snapshot ?? snapshot ?? "", fresh: result.fresh };
    };
    const promise = queue.then(run, run).catch(error => {
      if (ticket !== generation || !isCurrent()) return { status: 499, snapshot: snapshot ?? "" };
      throw error;
    });
    queue = promise.catch(() => {});
    return promise;
  };
  return {
    request,
    getHeader: () => header,
    subscribeHeader(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    hasPendingOtherThan(pageId: string) { return prepared !== null && prepared.pageId !== pageId; },
    cancel() {
      // Do not abort a running store write: it must finish in the same lane.
      // Its result and any not-yet-started requests are no longer current.
      generation++;
      prepared = null;
      publishHeader(null);
    },
    begin(pageId: string, snapshot?: string) {
      generation++;
      publishHeader(null);
      const promise = request(pageId, snapshot);
      // A failed/cancelled navigation may never mount a consumer.
      void promise.catch(() => {});
      prepared = { pageId, promise };
    },
    take(pageId: string) {
      if (prepared?.pageId !== pageId) return null;
      const promise = prepared.promise;
      prepared = null;
      return promise;
    },
  };
}

export const workspaceActivations = createWorkspaceActivationQueue((...args) => fetch(...args));
const tags = new Map<string, { snapshot: string; expires: number }>();

/** Compact freshness tags only, never document payloads or mounted pages.
 * Pages prepared without a tag are verified against the route's own tag. */
export function rememberWorkspaceNavigationTags(pages: readonly { id: string; snapshot: string | null }[]): void {
  const previous = new Map(tags);
  tags.clear();
  for (const page of pages.filter((page): page is { id: string; snapshot: string } => !!page.snapshot).slice(0, WORKSPACE_PREPARE_TAGGED)) {
    const href = `/page/${encodeURIComponent(page.id)}`;
    const old = previous.get(href);
    // Keep the tag paired with the original Next prefetch lifetime. Otherwise
    // a newer manifest plus an older RSC may require two foreground checks.
    tags.set(href, old && old.expires > Date.now() ? old : { snapshot: page.snapshot, expires: Date.now() + WORKSPACE_PREPARE_TTL_MS });
  }
}

/** Called by Next Link.onNavigate, not pointerdown/hover or modifier clicks. */
export function beginPreparedWorkspaceNavigation(href: string): void {
  announceWorkspaceNavigation(href);
  const pageId = workspacePageId(href);
  if (!pageId) {
    if (workspaceHomePath(href)) { cancelWorkspacePageWarmup(); workspaceActivations.cancel(); }
    return;
  }
  // Returning to a still-mounted page must also undo an early store switch.
  // Keep that write in the same lane so a late abandoned request cannot win.
  if (pageId === workspacePageId(window.location.pathname) && !workspaceActivations.hasPendingOtherThan(pageId)) return;
  // Если домашняя ещё не успела подготовить код, начинаем по реальному клику,
  // не дожидаясь RSC. Импорт ничего не активирует и не меняет порядок записей.
  preloadWorkspacePageCode();
  const tag = tags.get(href);
  // Preparation is optional: all actual page clicks can start verification
  // alongside RSC. No writes are started by hover or speculative prefetch.
  workspaceActivations.begin(pageId, tag && tag.expires > Date.now() ? tag.snapshot : undefined);
}
